/**
 * Projection Engine
 * 
 * Calculates wealth projections in REAL (inflation-adjusted) dollars.
 * This is the core calculation engine for the Life Financial Planner.
 */

import type {
  LifePlanState,
  TimelineState,
  LifeEvent,
  LifePhase,
  WealthProjection,
  WealthProjectionPoint,
} from '@/types'

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface ProjectionConfig {
  inflationRate: number           // Annual inflation rate (default 2.5%)
  stockReturn: number             // Expected stock market return (default 7%)
  bondReturn: number              // Expected bond return (default 4%)
  realEstateAppreciation: number  // Real estate appreciation (default 3%)
  safeWithdrawalRate: number      // Retirement withdrawal rate (default 4%)
}

export const DEFAULT_CONFIG: ProjectionConfig = {
  inflationRate: 2.5,
  stockReturn: 7,
  bondReturn: 4,
  realEstateAppreciation: 3,
  safeWithdrawalRate: 4,
}

// ============================================================================
// INFLATION HELPERS
// ============================================================================

/**
 * Convert nominal dollars to real dollars (today's purchasing power)
 */
export function toRealDollars(
  nominalAmount: number,
  yearsFromNow: number,
  inflationRate: number = DEFAULT_CONFIG.inflationRate
): number {
  return nominalAmount / Math.pow(1 + inflationRate / 100, yearsFromNow)
}

/**
 * Convert real dollars to nominal dollars (future dollars)
 */
export function toNominalDollars(
  realAmount: number,
  yearsFromNow: number,
  inflationRate: number = DEFAULT_CONFIG.inflationRate
): number {
  return realAmount * Math.pow(1 + inflationRate / 100, yearsFromNow)
}

/**
 * Calculate real return (return after inflation)
 */
export function getRealReturn(
  nominalReturn: number,
  inflationRate: number = DEFAULT_CONFIG.inflationRate
): number {
  // Fisher equation: (1 + real) = (1 + nominal) / (1 + inflation)
  return ((1 + nominalReturn / 100) / (1 + inflationRate / 100) - 1) * 100
}

// ============================================================================
// COMPOUND GROWTH
// ============================================================================

/**
 * Calculate future value with annual contributions
 */
export function futureValue(
  presentValue: number,
  annualContribution: number,
  annualReturn: number,
  years: number
): number {
  const r = annualReturn / 100
  
  // Future value of present value
  const fvPV = presentValue * Math.pow(1 + r, years)
  
  // Future value of annuity (contributions at end of each year)
  const fvAnnuity = annualContribution > 0 && r > 0
    ? annualContribution * ((Math.pow(1 + r, years) - 1) / r)
    : annualContribution * years
  
  return fvPV + fvAnnuity
}

/**
 * Calculate present value needed to reach a goal
 */
export function presentValueNeeded(
  futureGoal: number,
  annualContribution: number,
  annualReturn: number,
  years: number
): number {
  const r = annualReturn / 100
  
  // Present value of goal
  const pvGoal = futureGoal / Math.pow(1 + r, years)
  
  // Present value of annuity contributions
  const pvAnnuity = annualContribution > 0 && r > 0
    ? annualContribution * ((1 - Math.pow(1 + r, -years)) / r)
    : annualContribution * years
  
  return Math.max(0, pvGoal - pvAnnuity)
}

// ============================================================================
// MAIN PROJECTION ENGINE
// ============================================================================

export interface ProjectionInput {
  lifePlan: LifePlanState
  timeline: TimelineState
  config?: Partial<ProjectionConfig>
}

/**
 * Generate a complete wealth projection from the life plan and timeline
 */
export function generateWealthProjection(input: ProjectionInput): WealthProjection {
  const { lifePlan, timeline, config = {} } = input
  const fullConfig = { ...DEFAULT_CONFIG, ...config }
  
  // Determine projection range
  const currentYear = new Date().getFullYear()
  const birthYear = lifePlan.personal.birthDate 
    ? new Date(lifePlan.personal.birthDate).getFullYear() 
    : currentYear - 30
  const currentAge = currentYear - birthYear
  const endYear = birthYear + lifePlan.personal.lifeExpectancy
  
  // Initialize projection points
  const points: WealthProjectionPoint[] = []
  
  // Get initial values
  let totalAssets = lifePlan.computed.totalAssets
  let totalDebts = lifePlan.computed.totalDebts
  let annualIncome = lifePlan.computed.monthlyIncome * 12
  let annualExpenses = lifePlan.computed.monthlyExpenses * 12
  
  // Separate asset types for different growth rates
  let liquidAssets = lifePlan.assets
    .filter(a => ['cash', 'checking', 'savings', 'money_market', 'brokerage'].includes(a.category))
    .reduce((sum, a) => sum + a.currentValue, 0)
  
  let retirementAssets = lifePlan.assets
    .filter(a => a.isRetirementAccount)
    .reduce((sum, a) => sum + a.currentValue, 0)
  
  let realEstateEquity = lifePlan.assets
    .filter(a => a.category === 'real_estate')
    .reduce((sum, a) => sum + a.currentValue, 0)
  
  // Annual contributions to different account types
  const annualRetirementContribution = lifePlan.assets
    .filter(a => a.isRetirementAccount)
    .reduce((sum, a) => sum + a.contributions, 0)
  
  const annualLiquidContribution = lifePlan.assets
    .filter(a => !a.isRetirementAccount && a.category !== 'real_estate')
    .reduce((sum, a) => sum + a.contributions, 0)
  
  // Get events by year for quick lookup
  const eventsByYear = new Map<number, LifeEvent[]>()
  timeline.events.forEach(event => {
    const eventYear = new Date(event.date).getFullYear()
    if (!eventsByYear.has(eventYear)) {
      eventsByYear.set(eventYear, [])
    }
    eventsByYear.get(eventYear)!.push(event)
  })
  
  // Get current phase
  const getCurrentPhase = (year: number): LifePhase | undefined => {
    return timeline.phases.find(p => {
      const startYear = new Date(p.startDate).getFullYear()
      const endYear = new Date(p.endDate).getFullYear()
      return year >= startYear && year <= endYear
    })
  }
  
  // Real return rates (after inflation)
  const realStockReturn = getRealReturn(fullConfig.stockReturn, fullConfig.inflationRate)
  const realBondReturn = getRealReturn(fullConfig.bondReturn, fullConfig.inflationRate)
  const realREReturn = getRealReturn(fullConfig.realEstateAppreciation, fullConfig.inflationRate)
  
  // Blended return for liquid assets (60/40 stocks/bonds)
  const liquidReturn = (realStockReturn * 0.6 + realBondReturn * 0.4) / 100
  
  // Retirement account return (more aggressive)
  const retirementReturn = (realStockReturn * 0.8 + realBondReturn * 0.2) / 100
  
  // Real estate return
  const realEstateReturn = realREReturn / 100
  
  // Generate projection for each year
  for (let year = currentYear; year <= endYear; year++) {
    const yearsFromNow = year - currentYear
    const age = currentAge + yearsFromNow
    
    // Get events for this year
    const yearEvents = eventsByYear.get(year) || []
    
    // Get current phase budget
    const currentPhase = getCurrentPhase(year)
    
    // Apply events to finances
    yearEvents.forEach(event => {
      const impact = event.financialImpact
      
      // One-time impacts
      if (impact.oneTimeIncome) {
        liquidAssets += impact.oneTimeIncome
      }
      if (impact.oneTimeExpense) {
        liquidAssets -= impact.oneTimeExpense
      }
      
      // Recurring changes
      if (impact.incomeChange) {
        annualIncome += impact.incomeChange
      }
      if (impact.expenseChange) {
        annualExpenses += impact.expenseChange
      }
      
      // Asset/debt changes
      if (impact.assetChange) {
        liquidAssets += impact.assetChange
      }
      if (impact.debtChange) {
        totalDebts += impact.debtChange
      }
    })
    
    // Apply growth to assets (in real terms, growth is already inflation-adjusted)
    liquidAssets = liquidAssets * (1 + liquidReturn) + annualLiquidContribution
    retirementAssets = retirementAssets * (1 + retirementReturn) + annualRetirementContribution
    realEstateEquity = realEstateEquity * (1 + realEstateReturn)
    
    // Calculate debt payoff (simplified - assumes minimum payments reduce debt)
    const annualDebtPayment = lifePlan.debts.reduce(
      (sum, d) => sum + (d.minimumPayment + d.extraPayment) * 12, 
      0
    )
    totalDebts = Math.max(0, totalDebts - annualDebtPayment * 0.7) // Roughly 70% goes to principal
    
    // Update total assets
    totalAssets = liquidAssets + retirementAssets + realEstateEquity
    
    // Calculate annual savings
    const annualSavings = annualIncome - annualExpenses
    
    // Calculate net worth
    const netWorth = totalAssets - totalDebts
    
    // Calculate nominal (future) value for comparison
    const nominalNetWorth = toNominalDollars(netWorth, yearsFromNow, fullConfig.inflationRate)
    
    // Simple confidence bands (±20% for optimistic/pessimistic)
    const optimistic = netWorth * 1.2
    const pessimistic = netWorth * 0.8
    
    // Create projection point
    const point: WealthProjectionPoint = {
      year,
      date: `${year}-01-01`,
      age,
      netWorth: Math.round(netWorth),
      nominalNetWorth: Math.round(nominalNetWorth),
      totalAssets: Math.round(totalAssets),
      totalDebts: Math.round(totalDebts),
      liquidAssets: Math.round(liquidAssets),
      retirementAssets: Math.round(retirementAssets),
      realEstateEquity: Math.round(realEstateEquity),
      annualIncome: Math.round(annualIncome),
      annualExpenses: Math.round(annualExpenses),
      annualSavings: Math.round(annualSavings),
      eventsThisYear: yearEvents.map(e => e.id),
      phaseId: currentPhase?.id || '',
      optimistic: Math.round(optimistic),
      pessimistic: Math.round(pessimistic),
    }
    
    points.push(point)
  }
  
  return {
    points,
    startYear: currentYear,
    endYear,
    assumptions: {
      inflationRate: fullConfig.inflationRate,
      stockReturn: fullConfig.stockReturn,
      bondReturn: fullConfig.bondReturn,
      realEstateAppreciation: fullConfig.realEstateAppreciation,
    },
  }
}

// ============================================================================
// RETIREMENT CALCULATIONS
// ============================================================================

/**
 * Calculate retirement readiness score (0-100)
 */
export function calculateRetirementReadiness(
  projection: WealthProjection,
  lifePlan: LifePlanState
): number {
  const { retirementAge } = lifePlan.personal
  const { expectedSocialSecurity, hasPension, pensionMonthly } = lifePlan.preferences
  
  const currentYear = new Date().getFullYear()
  const birthYear = lifePlan.personal.birthDate 
    ? new Date(lifePlan.personal.birthDate).getFullYear() 
    : currentYear - 30
  
  const retirementYear = birthYear + retirementAge
  const retirementPoint = projection.points.find(p => p.year === retirementYear)
  
  if (!retirementPoint) return 0
  
  // Annual retirement income needed (80% of current expenses is a common rule)
  const currentExpenses = projection.points[0]?.annualExpenses || 50000
  const retirementExpensesNeeded = currentExpenses * 0.8
  
  // Social Security and pension income
  const guaranteedIncome = (expectedSocialSecurity * 12) + (hasPension ? pensionMonthly * 12 : 0)
  
  // Income gap that needs to come from savings
  const incomeGap = Math.max(0, retirementExpensesNeeded - guaranteedIncome)
  
  // Portfolio needed at retirement (using 4% withdrawal rate)
  const portfolioNeeded = incomeGap / 0.04
  
  // What they'll actually have
  const portfolioAtRetirement = retirementPoint.netWorth
  
  // Readiness score
  if (portfolioNeeded <= 0) return 100
  
  const readiness = Math.min(100, (portfolioAtRetirement / portfolioNeeded) * 100)
  return Math.round(readiness)
}

/**
 * Calculate how many years of retirement the portfolio will fund
 */
export function calculateRetirementYearsFunded(
  projection: WealthProjection,
  lifePlan: LifePlanState,
  config: ProjectionConfig = DEFAULT_CONFIG
): number {
  const { retirementAge } = lifePlan.personal
  const { expectedSocialSecurity, hasPension, pensionMonthly } = lifePlan.preferences
  
  const currentYear = new Date().getFullYear()
  const birthYear = lifePlan.personal.birthDate 
    ? new Date(lifePlan.personal.birthDate).getFullYear() 
    : currentYear - 30
  
  const retirementYear = birthYear + retirementAge
  const retirementPoint = projection.points.find(p => p.year === retirementYear)
  
  if (!retirementPoint) return 0
  
  let portfolioValue = retirementPoint.netWorth
  const currentExpenses = projection.points[0]?.annualExpenses || 50000
  const retirementExpenses = currentExpenses * 0.8
  const guaranteedIncome = (expectedSocialSecurity * 12) + (hasPension ? pensionMonthly * 12 : 0)
  const annualWithdrawal = Math.max(0, retirementExpenses - guaranteedIncome)
  
  // Real return in retirement (more conservative)
  const realReturn = getRealReturn(
    config.stockReturn * 0.5 + config.bondReturn * 0.5,
    config.inflationRate
  ) / 100
  
  let years = 0
  while (portfolioValue > 0 && years < 100) {
    portfolioValue = portfolioValue * (1 + realReturn) - annualWithdrawal
    years++
  }
  
  return years
}

// ============================================================================
// GOAL TRACKING
// ============================================================================

/**
 * Calculate years until a goal can be achieved
 */
export function calculateYearsToGoal(
  currentSavings: number,
  goalAmount: number,
  annualContribution: number,
  annualReturn: number = 5
): number {
  if (currentSavings >= goalAmount) return 0
  if (annualContribution <= 0) return Infinity
  
  const r = annualReturn / 100
  const target = goalAmount - currentSavings
  
  // Solve for n in: FV = C * ((1+r)^n - 1) / r
  // n = ln((FV * r / C) + 1) / ln(1 + r)
  if (r > 0) {
    const n = Math.log((target * r / annualContribution) + 1) / Math.log(1 + r)
    return Math.ceil(n)
  }
  
  return Math.ceil(target / annualContribution)
}

/**
 * Calculate monthly contribution needed to reach a goal
 */
export function calculateMonthlyContributionNeeded(
  currentSavings: number,
  goalAmount: number,
  yearsToGoal: number,
  annualReturn: number = 5
): number {
  if (yearsToGoal <= 0) return 0
  if (currentSavings >= goalAmount) return 0
  
  const r = annualReturn / 100
  const n = yearsToGoal
  
  // Future value of current savings
  const fvCurrent = currentSavings * Math.pow(1 + r, n)
  
  // Amount needed from contributions
  const needed = goalAmount - fvCurrent
  if (needed <= 0) return 0
  
  // Solve for annual contribution
  // needed = C * ((1+r)^n - 1) / r
  const annualContribution = r > 0
    ? (needed * r) / (Math.pow(1 + r, n) - 1)
    : needed / n
  
  return Math.max(0, annualContribution / 12)
}

// ============================================================================
// EXPORT DEFAULT CONFIG FOR EASY ACCESS
// ============================================================================

export { DEFAULT_CONFIG as ProjectionDefaults }
